import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AddressSuggestion,
  autocompleteAddress,
  getReachableLocations,
  JourneyData,
} from './services/tflApi';

interface Props {
  onResult: (ids: string[], journeyMap: Map<string, JourneyData>) => void;
  onClear: () => void;
}

export default function CommuteTimeFilter({
  onResult,
  onClear,
}: Props) {
  const [addressText, setAddressText] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedDest, setSelectedDest] = useState<{ lat: number; lng: number } | null>(null);
  const [maxMinutes, setMaxMinutes] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (selectedDest !== null) return;
    if (!addressText) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const results = await autocompleteAddress(addressText);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [addressText, selectedDest]);

  useEffect(() => {
    if (selectedDest === null || maxMinutes === null) return;

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    getReachableLocations(selectedDest, maxMinutes)
      .then((results) => {
        if (cancelled) return;
        setIsLoading(false);
        if (results.length === 0) {
          setError(
            `No areas found within ${maxMinutes} minutes of that address. Try a longer commute time or a different address.`
          );
          onResult([], new Map());
        } else {
          setError(null);
          const ids = results.map((r) => r.id);
          const journeyMap = new Map(results.map((r) => [r.id, r.journey]));
          onResult(ids, journeyMap);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
        setError(
          "Couldn't calculate travel times right now. Please try again or use the Neighbourhood filter."
        );
        onResult([], new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDest, maxMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddressText(e.target.value);
      setSelectedDest(null);
      onClear();
      setError(null);
    },
    [onClear]
  );

  const handleSuggestionSelect = useCallback((s: AddressSuggestion) => {
    setAddressText(s.label);
    setSelectedDest({ lat: s.lat, lng: s.lng });
    setShowSuggestions(false);
    setSuggestions([]);
  }, []);

  return (
    <div className="commute-filter">
      <div className="commute-filter__field">
        <label className="commute-filter__label" htmlFor="commute-address">
          Destination address
        </label>
        <div className="commute-filter__address-wrapper">
          <input
            id="commute-address"
            type="text"
            className="commute-filter__input"
            value={addressText}
            onChange={handleAddressChange}
            placeholder="e.g. Canary Wharf, London"
            role="combobox"
            aria-label="Destination address"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-haspopup="listbox"
            aria-controls="commute-address-suggestions"
            onBlur={() => setShowSuggestions(false)}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul id="commute-address-suggestions" className="address-suggestions" role="listbox">
              {suggestions.map((s, i) => (
                <li
                  key={i}
                  role="option"
                  aria-selected={false}
                  className="address-suggestions__item"
                  onPointerDown={(e) => { e.preventDefault(); handleSuggestionSelect(s); }}
                >
                  {s.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="commute-filter__field">
        <label className="commute-filter__label" htmlFor="commute-time">
          Maximum commute time
        </label>
        <select
          id="commute-time"
          className="commute-filter__select"
          value={maxMinutes ?? ''}
          onChange={(e) => setMaxMinutes(e.target.value ? Number(e.target.value) : null)}
          aria-label="Maximum commute time"
        >
          <option value="" disabled>Select commute time</option>
          {[15, 20, 30, 45, 60, 90].map((m) => (
            <option key={m} value={m}>
              {m} minutes
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="commute-loading" role="status" aria-live="polite">
          Calculating travel times...
        </div>
      )}
      {error && !isLoading && (
        <div className="commute-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
